import {ArrowBothIcon, ColumnsIcon, ListUnorderedIcon, NoteIcon, ProjectIcon, RowsIcon} from '@primer/octicons-react'
import {AriaAlert, Label, Spinner} from '@primer/react'
import {useMemo, useState} from 'react'

import type {ViewConfiguration} from '../../contexts/views/view-configuration'
import type {Layout} from '../../utils/saved-view-url-params'
import {getFieldsForCapability} from './get-fields-for-capability'
import {Capability, type FieldDefinition, type FieldsData} from './fields'
import type {FieldOption} from '../view-options/field-option'
import {ViewOptionsMenu} from '../view-options/ViewOptionsMenu'
import {getFieldIcon} from './field-icons'
import styles from './SavedViewOptionsMenu.module.css'

const LAYOUT_OPTIONS = [
  {value: 'list', label: 'List', icon: ListUnorderedIcon},
  {value: 'board', label: 'Board', icon: ProjectIcon},
] satisfies ReadonlyArray<{value: Layout; label: string; icon: typeof ListUnorderedIcon}>

function toFieldOption({identifier, label, icon}: FieldDefinition): FieldOption {
  return {identifier, label, icon: getFieldIcon(icon)}
}

/** Sort's leading visual, matching memex: a two-way arrow rotated to point up/down. */
function SortByIcon({size}: {size?: number}) {
  return <ArrowBothIcon size={size} className={styles.sortIcon} />
}

type ViewConfigurationDirtyState = {[Key in keyof ViewConfiguration]: boolean}

interface SavedViewOptionsMenuProps {
  fields: FieldsData | undefined
  /** True while the field definitions are being fetched asynchronously. */
  isPending?: boolean
  /** True when the async fetch for the field definitions failed. */
  isError?: boolean
  configuration: ViewConfiguration
  showDirtyIndicator?: boolean
  /** Applied settings that differ from the saved baseline, not the dialog draft. */
  configurationDirty?: ViewConfigurationDirtyState
  /** Receives changed settings together on Apply, including the full visible-field selection when changed. */
  onApply: (changes: Partial<ViewConfiguration>) => void
}

/**
 * Locally-editable draft copy of the pieces of a `ViewConfiguration` this menu exposes.
 * Kept flat and keyed on the identifiers the section controls report — `sorts` collapses
 * to a single field because Sort by is single-select in the UI.
 */
type Draft = {
  layout: Layout
  groupBy: string | null
  swimlaneBy: string | null
  sortField: string | null
  visibleFields: ReadonlySet<string>
}

function toDraft(configuration: ViewConfiguration): Draft {
  return {
    layout: configuration.layout,
    groupBy: configuration.groupBy,
    swimlaneBy: configuration.swimlaneBy,
    sortField: configuration.sorts[0]?.field ?? null,
    visibleFields: new Set(configuration.visibleFields),
  }
}

/** Controlled saved-view options menu shared by domain-specific hosts. */
export function SavedViewOptionsMenu({
  fields: view,
  isPending = false,
  isError = false,
  configuration,
  showDirtyIndicator = false,
  configurationDirty,
  onApply,
}: SavedViewOptionsMenuProps) {
  // Draft copy of the pieces the surface can edit. Sections read from and write to the
  // draft; nothing propagates to the host until Apply. `onOpenChange` resyncs the draft
  // on open so an external change to `configuration` (e.g. someone else navigated) is not
  // shadowed by a stale draft; every dismissal also resets via `onCancel`.
  const [draft, setDraft] = useState<Draft>(() => toDraft(configuration))
  const appliedSortField = configuration.sorts[0]?.field ?? null

  const isVisibilityDirty =
    draft.visibleFields.size !== configuration.visibleFields.length ||
    configuration.visibleFields.some(identifier => !draft.visibleFields.has(identifier))
  const isDirty =
    draft.layout !== configuration.layout ||
    draft.groupBy !== configuration.groupBy ||
    draft.swimlaneBy !== configuration.swimlaneBy ||
    draft.sortField !== appliedSortField ||
    isVisibilityDirty

  const groupFields = useMemo(
    () => (view ? getFieldsForCapability(view, Capability.GROUPABLE).map(toFieldOption) : []),
    [view],
  )
  const columnFields = useMemo(
    () => groupFields.map(field => ({...field, disabled: field.identifier === draft.swimlaneBy})),
    [groupFields, draft.swimlaneBy],
  )
  const swimlaneFields = useMemo(
    () => groupFields.map(field => ({...field, disabled: field.identifier === draft.groupBy})),
    [groupFields, draft.groupBy],
  )
  const attributeFields = useMemo(
    () => (view ? getFieldsForCapability(view, Capability.ATTRIBUTABLE).map(toFieldOption) : []),
    [view],
  )
  const sortFields = useMemo(
    () => (view ? getFieldsForCapability(view, Capability.SORTABLE).map(toFieldOption) : []),
    [view],
  )

  const toggleVisible = (identifier: string, next: boolean) =>
    setDraft(current => {
      const updated = new Set(current.visibleFields)
      if (next) updated.add(identifier)
      else updated.delete(identifier)
      return {...current, visibleFields: updated}
    })

  const commit = () => {
    const changes: Partial<ViewConfiguration> = {}
    if (draft.layout !== configuration.layout) changes.layout = draft.layout
    if (draft.groupBy !== configuration.groupBy && draft.groupBy !== null) changes.groupBy = draft.groupBy
    if (draft.swimlaneBy !== configuration.swimlaneBy) changes.swimlaneBy = draft.swimlaneBy
    if (draft.sortField !== appliedSortField) {
      changes.sorts = draft.sortField === null ? [] : [{field: draft.sortField, direction: 'asc'}]
    }
    if (isVisibilityDirty) changes.visibleFields = [...draft.visibleFields]
    if (Object.keys(changes).length > 0) onApply(changes)
  }

  return (
    <ViewOptionsMenu
      triggerLabel="View options"
      title="View options"
      onApply={commit}
      onCancel={() => setDraft(toDraft(configuration))}
      onOpenChange={open => {
        if (open) setDraft(toDraft(configuration))
      }}
      isDirty={isDirty}
      showDirtyIndicator={showDirtyIndicator}
    >
      <ViewOptionsMenu.Layout
        label="Layout"
        dirtyValue={configurationDirty?.layout ? configuration.layout : undefined}
        trailing={draft.layout === 'board' ? <Label variant="success">Alpha</Label> : undefined}
        options={LAYOUT_OPTIONS}
        value={draft.layout}
        onSelect={next => setDraft(current => ({...current, layout: next}))}
      />

      {draft.layout === 'board' && (
        <ViewOptionsMenu.Group
          label="Configuration"
          status={
            isPending ? (
              <div className={styles.status}>
                <Spinner srText="Loading view options" />
              </div>
            ) : isError ? (
              <AriaAlert className={styles.status}>Unable to load view options</AriaAlert>
            ) : null
          }
        >
          {view && (
            <>
              <ViewOptionsMenu.MultiSelect
                icon={NoteIcon}
                label="Visible fields"
                showDirtyIndicator={configurationDirty?.visibleFields}
                placeholder="None"
                fields={attributeFields}
                selectedIds={draft.visibleFields}
                onToggle={toggleVisible}
              />
              <ViewOptionsMenu.SingleSelect
                icon={ColumnsIcon}
                label="Column by"
                showDirtyIndicator={configurationDirty?.groupBy}
                placeholder="None"
                fields={columnFields}
                value={draft.groupBy}
                onSelect={next =>
                  setDraft(current => ({
                    ...current,
                    groupBy: next,
                    // If the picked field is also the current swimlane, clear the swimlane
                    // so the two axes never collide, matching the previous direct mapping.
                    swimlaneBy: current.swimlaneBy === next ? null : current.swimlaneBy,
                  }))
                }
              />
              <ViewOptionsMenu.SingleSelect
                icon={RowsIcon}
                label="Swimlane by"
                showDirtyIndicator={configurationDirty?.swimlaneBy}
                placeholder="None"
                fields={swimlaneFields}
                value={draft.swimlaneBy}
                onSelect={next => setDraft(current => ({...current, swimlaneBy: next}))}
                onClear={() => setDraft(current => ({...current, swimlaneBy: null}))}
              />
              <ViewOptionsMenu.SingleSelect
                icon={SortByIcon}
                label="Sort by"
                showDirtyIndicator={configurationDirty?.sorts}
                placeholder="None"
                fields={sortFields}
                value={draft.sortField}
                onSelect={next => setDraft(current => ({...current, sortField: next}))}
                onClear={() => setDraft(current => ({...current, sortField: null}))}
              />
            </>
          )}
        </ViewOptionsMenu.Group>
      )}
    </ViewOptionsMenu>
  )
}
